import { useEffect, useMemo } from 'react';

import { Box } from '@mui/material';
import usePublisher from 'api/usePublisher';
import { EIntervalsRepeat } from 'constants/enums';
import dayjs from 'dayjs';

import Button from 'components/Button/Button';

interface ExchangeRatesUpdaterManagerProps {
  setUpdateExchangeRatesDialogOpen: (open: boolean) => void;
  disabled?: boolean;
}

const ExchangeRatesUpdaterManager: React.FC<
  ExchangeRatesUpdaterManagerProps
> = ({ setUpdateExchangeRatesDialogOpen, disabled }) => {
  const { getPublisherSettings } = usePublisher();

  const nextUpdateDate = useMemo(() => {
    if (!getPublisherSettings.data) return '';

    const scheduler = getPublisherSettings.data.exchangeRatesUpdateScheduler;
    if (!scheduler || scheduler.frequency === EIntervalsRepeat.NONE) {
      return 'Not scheduled';
    }

    const now = dayjs();
    let nextUpdate: dayjs.Dayjs;

    switch (scheduler.frequency) {
      case EIntervalsRepeat.DAILY:
        nextUpdate = now.hour(scheduler.hour).minute(0).second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'day');
        }
        break;

      case EIntervalsRepeat.WEEKLY:
        const targetWeekday = scheduler.weeklyFrequencyFactor;
        nextUpdate = now
          .day(targetWeekday)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'week');
        }
        break;

      case EIntervalsRepeat.MONTHLY:
        const targetDay = scheduler.monthlyFrequencyFactor;
        nextUpdate = now
          .date(targetDay)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'month');
        }
        break;

      case EIntervalsRepeat.QUARTERLY:
        const currentQuarter = Math.floor(now.month() / 3);
        const quarterMonths = [0, 3, 6, 9];
        const targetMonthQuarterly = quarterMonths[currentQuarter];

        nextUpdate = now
          .month(targetMonthQuarterly)
          .date(1)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);

        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(3, 'month');
        }
        break;

      case EIntervalsRepeat.SEMI_ANNUALLY:
        const currentSemiAnnually = Math.floor(now.month() / 6);
        const semiAnnuallyMonths = [0, 6];
        const targetMonthSemiAnnually = semiAnnuallyMonths[currentSemiAnnually];

        nextUpdate = now
          .month(targetMonthSemiAnnually)
          .date(1)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);

        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(6, 'month');
        }
        break;

      case EIntervalsRepeat.ANNUALLY:
        nextUpdate = now
          .month(0)
          .date(1)
          .hour(scheduler.hour)
          .minute(0)
          .second(0);
        if (nextUpdate.isBefore(now)) {
          nextUpdate = nextUpdate.add(1, 'year');
        }
        break;

      default:
        return 'Not scheduled';
    }

    return nextUpdate.format('MMM DD, YYYY [at] HH:mm UTC');
  }, [getPublisherSettings.data]);

  useEffect(() => {
    if (!getPublisherSettings.data) {
      getPublisherSettings.refetch();
    }
  }, [getPublisherSettings.data, getPublisherSettings]);

  return (
    <Box className="settings-card mt-4 flex flex-row items-center justify-between p-9">
      <Box style={{ maxWidth: '50%' }}>
        <h3 className="text-base-cardForeground font-semibold">
          Update Exchange Rates
        </h3>
        <p className="mt-2 small-text">
          Keep exchange rates current by either refreshing them manually or
          scheduling automatic updates. You can schedule these to occur daily,
          weekly, or monthly. Prices will adjust according to the latest rates.
        </p>
      </Box>
      <Box>
        <Box className="buttons-wrapper justify-end">
          <Button
            onClick={() => setUpdateExchangeRatesDialogOpen(true)}
            variant="default"
            type="button"
            disabled={disabled}
          >
            Update
          </Button>
        </Box>
        <Box mt={1}>
          <span className="small-text">Next update: {nextUpdateDate}</span>
        </Box>
      </Box>
    </Box>
  );
};

export default ExchangeRatesUpdaterManager;
